import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { HERO } from '@/constants/testIds';
import { Sparkles, ArrowRight, Loader2, Play, BadgeCheck, TrendingUp, Globe } from 'lucide-react';
import api from '@/lib/api';

// ----- cycling AI search examples (typed effect) -----
const EXAMPLES = [
  'Punjabi wedding singer in Dubai under ₹2L',
  'Bollywood DJ for New Year party in Goa',
  'Contemporary dancer for OTT music video',
  'Method actor for streaming series pilot',
  'Female playback singer for brand anthem',
];

// ----- live activity ticker -----
const ACTIVITY = [
  { name: 'Aarav', action: 'booked for a luxury wedding', city: 'Dubai', amount: '₹3.2L' },
  { name: 'Naya', action: 'confirmed a brand shoot', city: 'Mumbai', amount: '₹1.5L' },
  { name: 'DJ Kabir', action: 'sold out a festival slot', city: 'Goa', amount: '₹6L' },
  { name: 'Ishaan', action: 'shortlisted for a series', city: 'Mumbai', amount: 'pilot' },
  { name: 'Liana', action: 'walked Lakmé runway', city: 'Mumbai', amount: '₹2L' },
  { name: 'Reuben', action: 'shot a Vogue editorial', city: 'Bali', amount: '₹4L' },
];

// ----- floating mosaic artist tiles -----
const MOSAIC = [
  {
    src: 'https://images.unsplash.com/photo-1526218626217-dc65a29bb444?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwxfHxzaW5nZXIlMjBuZW9uJTIwc3RhZ2UlMjBjb25jZXJ0fGVufDB8fHx8MTc4MDQyNzIzOXww&ixlib=rb-4.1.0&q=85',
    label: 'Aarav · Singer',
    badge: 'Elite',
    pos: 'top-[6%] left-[8%]',
    size: 'w-44 h-56',
    rotate: '-rotate-6',
    delay: '0s',
    ring: 'from-fuchsia-500 to-purple-700',
  },
  {
    src: 'https://images.pexels.com/photos/4310835/pexels-photo-4310835.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    label: 'Naya · Dancer',
    badge: 'Pro',
    pos: 'top-[2%] right-[6%]',
    size: 'w-40 h-52',
    rotate: 'rotate-3',
    delay: '0.8s',
    ring: 'from-pink-500 to-rose-600',
  },
  {
    src: 'https://images.unsplash.com/photo-1506157786151-b8491531f063?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NjZ8MHwxfHNlYXJjaHwxfHxkaiUyMG11c2ljJTIwZmVzdGl2YWwlMjBjcm93ZHxlbnwwfHx8fDE3ODA0MjcyMzl8MA&ixlib=rb-4.1.0&q=85',
    label: 'DJ Kabir',
    badge: 'Elite',
    pos: 'top-[44%] right-[24%]',
    size: 'w-52 h-44',
    rotate: '-rotate-2',
    delay: '1.6s',
    ring: 'from-cyan-500 to-blue-700',
  },
  {
    src: 'https://images.pexels.com/photos/37105844/pexels-photo-37105844.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    label: 'Ishaan · Actor',
    badge: 'Pro',
    pos: 'bottom-[6%] left-[14%]',
    size: 'w-40 h-52',
    rotate: 'rotate-6',
    delay: '0.4s',
    ring: 'from-orange-500 to-red-600',
  },
  {
    src: 'https://images.pexels.com/photos/33847264/pexels-photo-33847264.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    label: 'Liana · Model',
    badge: 'Pro',
    pos: 'bottom-[10%] right-[8%]',
    size: 'w-44 h-56',
    rotate: '-rotate-3',
    delay: '1.2s',
    ring: 'from-amber-400 to-orange-500',
  },
  {
    src: 'https://images.pexels.com/photos/35057828/pexels-photo-35057828.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    label: 'Reuben · Photo',
    badge: 'Pro',
    pos: 'top-[26%] left-[34%]',
    size: 'w-36 h-44',
    rotate: 'rotate-2',
    delay: '2s',
    ring: 'from-emerald-400 to-teal-600',
  },
];

// ----- equalizer bars (left & right edges) -----
const EqualizerBar = ({ delay, height }) => (
  <div
    className="w-1 rounded-full bg-gradient-to-t from-fuchsia-500 via-purple-500 to-cyan-400"
    style={{ height, animation: `eq-bounce ${0.9 + (delay % 5) * 0.12}s ease-in-out ${delay}s infinite alternate` }}
  />
);

// ----- typed cycler hook -----
const useTypedExample = () => {
  const [text, setText] = useState('');
  const [idx, setIdx] = useState(0);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const target = EXAMPLES[idx];
    let t;
    if (!deleting && text === target) {
      t = setTimeout(() => setDeleting(true), 1800);
    } else if (deleting && text === '') {
      setDeleting(false);
      setIdx((i) => (i + 1) % EXAMPLES.length);
    } else {
      t = setTimeout(() => {
        setText((prev) => deleting ? prev.slice(0, -1) : target.slice(0, prev.length + 1));
      }, deleting ? 28 : 55);
    }
    return () => clearTimeout(t);
  }, [text, idx, deleting]);

  return text;
};

const HeroSection = () => {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [tickerIdx, setTickerIdx] = useState(0);
  const heroRef = useRef(null);
  const typed = useTypedExample();

  useEffect(() => {
    const t = setInterval(() => setTickerIdx((i) => (i + 1) % ACTIVITY.length), 3200);
    return () => clearInterval(t);
  }, []);

  // mouse parallax for mosaic
  useEffect(() => {
    const node = heroRef.current;
    if (!node) return;
    const handle = (e) => {
      const rect = node.getBoundingClientRect();
      const dx = (e.clientX - rect.left - rect.width / 2) / rect.width;
      const dy = (e.clientY - rect.top - rect.height / 2) / rect.height;
      node.style.setProperty('--mx', dx.toFixed(3));
      node.style.setProperty('--my', dy.toFixed(3));
    };
    node.addEventListener('mousemove', handle);
    return () => node.removeEventListener('mousemove', handle);
  }, []);

  const search = async (e) => {
    e?.preventDefault?.();
    if (!query.trim()) return;
    setLoading(true);
    navigate(`/artists?ai=${encodeURIComponent(query)}`);
    setLoading(false);
  };

  const activity = ACTIVITY[tickerIdx];

  return (
    <section ref={heroRef} className="relative min-h-screen overflow-hidden noise-overlay">
      {/* Background gradient orbs */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute top-[10%] left-[-10%] h-[500px] w-[500px] rounded-full bg-fuchsia-600/30 blur-[120px] animate-float-slow" />
        <div className="absolute top-[30%] right-[-10%] h-[600px] w-[600px] rounded-full bg-cyan-500/25 blur-[140px] animate-float-slow" style={{ animationDelay: '2s' }} />
        <div className="absolute bottom-[-15%] left-[20%] h-[500px] w-[500px] rounded-full bg-orange-500/20 blur-[120px] animate-float-slow" style={{ animationDelay: '4s' }} />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_30%,#0B1020_85%)]" />
        {/* grid pattern */}
        <div className="absolute inset-0 opacity-[0.04]"
             style={{
               backgroundImage: 'linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)',
               backgroundSize: '60px 60px',
             }} />
      </div>

      {/* Top "LIVE NOW" ticker bar */}
      <div className="relative pt-24 pb-2">
        <div className="max-w-7xl mx-auto px-6 sm:px-12">
          <div className="inline-flex items-center gap-3 glass-strong rounded-full pl-2 pr-5 py-1.5 border border-white/10">
            <span className="relative inline-flex h-6 items-center gap-1.5 rounded-full bg-rose-500/20 text-rose-300 text-[10px] uppercase tracking-widest px-2.5 font-semibold">
              <span className="relative flex h-1.5 w-1.5">
                <span className="absolute inline-flex h-full w-full rounded-full bg-rose-400 opacity-75 animate-ping" />
                <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-rose-400" />
              </span>
              Live
            </span>
            <div key={tickerIdx} className="text-xs sm:text-sm text-slate-200 animate-fade-up">
              <span className="font-display text-white font-semibold">{activity.name}</span>
              <span className="text-slate-400"> {activity.action} in </span>
              <span className="text-fuchsia-300">{activity.city}</span>
              <span className="text-slate-500"> · {activity.amount}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Equalizer bars left/right */}
      <div className="absolute left-4 lg:left-10 top-1/3 hidden md:flex flex-col gap-1.5">
        {[18, 32, 12, 40, 26, 14].map((h, i) => <EqualizerBar key={`eq-l-${i}-${h}`} delay={i * 0.18} height={`${h}px`} />)}
      </div>
      <div className="absolute right-4 lg:right-10 top-1/2 hidden md:flex flex-col gap-1.5">
        {[28, 14, 36, 18, 44, 22].map((h, i) => <EqualizerBar key={`eq-r-${i}-${h}`} delay={i * 0.22 + 0.4} height={`${h}px`} />)}
      </div>

      {/* Main editorial split */}
      <div className="relative max-w-7xl mx-auto px-6 sm:px-12 pt-6 pb-8 grid lg:grid-cols-[1.05fr_0.95fr] gap-10 items-center">

        {/* LEFT: Editorial type + AI search */}
        <div className="relative z-10">
          <div className="inline-flex items-center gap-2 glass-strong rounded-full px-3.5 py-1.5 mb-7 text-[10px] uppercase tracking-[0.25em] text-slate-200 border border-white/10">
            <Sparkles className="h-3.5 w-3.5 text-fuchsia-300" />
            AI-powered talent universe
          </div>

          <h1 className="font-hero font-bold tracking-[-0.015em] leading-[1.02] text-white">
            <span className="hero-reveal block whitespace-nowrap text-[clamp(2rem,3.6vw,3.4rem)]" style={{ animationDelay: '0.05s', fontWeight: 500 }}>
              Every Event
            </span>
            <span className="hero-reveal block whitespace-nowrap text-[clamp(2.6rem,4.6vw,4.4rem)]" style={{ animationDelay: '0.22s' }}>
              <span className="rainbow-text-animated">Starts with</span>
            </span>
            <span className="hero-reveal block whitespace-nowrap text-[clamp(3rem,5.2vw,5.2rem)] font-italic-display" style={{ animationDelay: '0.4s' }}>
              <span className="ai-text">Talent.</span>
            </span>
          </h1>
          <div className="hero-underline mt-5 h-1 w-28 rounded-full bg-gradient-to-r from-fuchsia-500 via-orange-400 to-cyan-400" />

          <p className="mt-6 text-slate-300 text-base sm:text-lg max-w-xl leading-relaxed hero-reveal" style={{ animationDelay: '0.55s' }}>
            <span className="text-white font-medium">AI-powered global talent discovery &amp; booking platform</span> — find singers, dancers, DJs, actors, photographers and more, in seconds.
          </p>

          {/* AI search bar */}
          <form onSubmit={search} className="mt-9 relative max-w-xl">
            <div className="relative rounded-2xl glass-strong gradient-border shadow-[0_30px_80px_-20px_rgba(123,47,247,0.55)]">
              <div className="flex items-center p-2">
                <div className="pl-3 pr-1 text-fuchsia-300">
                  <Sparkles className="h-5 w-5" />
                </div>
                <div className="flex-1 relative">
                  <input
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    data-testid={HERO.searchInput}
                    className="w-full bg-transparent py-3.5 px-2 text-white text-base focus:outline-none font-body"
                    placeholder=""
                  />
                  {!query && (
                    <div className="absolute inset-0 flex items-center px-2 pointer-events-none">
                      <span className="text-slate-500 truncate">
                        Try: <span className="text-slate-300">"{typed}"</span>
                        <span className="ml-0.5 inline-block w-[2px] h-4 bg-fuchsia-400 animate-pulse" />
                      </span>
                    </div>
                  )}
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  data-testid={HERO.searchButton}
                  className="btn-rainbow rounded-xl px-5 py-2.5 text-white font-medium flex items-center gap-2 disabled:opacity-60"
                >
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
                  <span className="hidden sm:inline text-sm">Discover</span>
                </button>
              </div>
            </div>
            <p className="mt-2 text-[10px] uppercase tracking-[0.25em] text-slate-500">
              Claude Sonnet · Understands natural language
            </p>
          </form>

          {/* CTA buttons */}
          <div className="flex flex-wrap items-center gap-3 mt-8">
            <button
              onClick={() => navigate('/artists')}
              data-testid={HERO.ctaArtists}
              className="btn-rainbow rounded-full px-6 py-3 text-white font-medium inline-flex items-center gap-2 text-sm"
            >
              Browse talent <ArrowRight className="h-4 w-4" />
            </button>
            <button
              onClick={() => navigate('/register')}
              data-testid={HERO.ctaJoin}
              className="glass-strong rounded-full px-6 py-3 text-white font-medium hover:border-fuchsia-400/40 transition text-sm border border-white/10 inline-flex items-center gap-2"
            >
              <Play className="h-3.5 w-3.5 fill-white" /> Join as an artist
            </button>
          </div>

          {/* Trust row */}
          <div className="mt-10 flex items-center gap-5 text-slate-400 text-xs">
            <div className="flex -space-x-2">
              {MOSAIC.slice(0, 4).map((m) => (
                <img key={m.src} src={m.src} alt="" className="h-8 w-8 rounded-full border-2 border-[#0B1020] object-cover" />
              ))}
            </div>
            <div>
              <div className="flex items-center gap-1 text-amber-300">
                {'★★★★★'.split('').map((s, i) => <span key={`hero-star-${i}`}>{s}</span>)}
                <span className="text-slate-300 ml-1">4.9</span>
              </div>
              <span className="text-slate-500">Trusted by 12,000+ artists</span>
            </div>
          </div>
        </div>

        {/* RIGHT: Floating artist mosaic */}
        <div className="relative h-[640px] hidden lg:block" style={{ transform: 'translate3d(0,0,0)' }}>
          {MOSAIC.map((tile, i) => (
            <div
              key={tile.label}
              className={`absolute ${tile.pos} ${tile.size} ${tile.rotate} group cursor-pointer`}
              style={{
                animation: `mosaic-float 7s ease-in-out ${tile.delay} infinite alternate`,
                transform: `translate(calc(var(--mx,0) * ${(i % 2 ? -1 : 1) * 14}px), calc(var(--my,0) * ${(i % 2 ? -1 : 1) * 14}px))`,
                transition: 'transform 0.4s ease-out',
              }}
            >
              <div className="relative w-full h-full rounded-2xl overflow-hidden border border-white/15 shadow-[0_30px_80px_-30px_rgba(0,0,0,0.9)]">
                <div className={`absolute -inset-0.5 bg-gradient-to-br ${tile.ring} opacity-60 blur-md -z-10 group-hover:opacity-100 transition`} />
                <img src={tile.src} alt={tile.label} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent" />
                <div className="absolute top-2 right-2">
                  <span className="glass text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full text-amber-300 flex items-center gap-1">
                    <BadgeCheck className="h-2.5 w-2.5" /> {tile.badge}
                  </span>
                </div>
                <div className="absolute bottom-2 left-2 right-2">
                  <div className="text-white text-[11px] font-display font-semibold drop-shadow truncate">{tile.label}</div>
                </div>
              </div>
            </div>
          ))}

          {/* Floating "AI matching" badge */}
          <div className="absolute top-[36%] right-[42%] glass-strong rounded-2xl px-3.5 py-2 border border-cyan-400/30 shadow-2xl animate-pulse-glow">
            <div className="flex items-center gap-2 text-xs">
              <Sparkles className="h-3.5 w-3.5 text-cyan-300" />
              <span className="text-slate-200">AI matching</span>
              <span className="ai-text font-display font-bold text-sm">98%</span>
            </div>
          </div>

          {/* Floating booking confirmation */}
          <div className="absolute bottom-[24%] left-[2%] glass-strong rounded-2xl px-3.5 py-2 border border-emerald-400/30 shadow-2xl">
            <div className="flex items-center gap-2 text-xs">
              <div className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse" />
              <span className="text-slate-300">Booking confirmed</span>
            </div>
          </div>
        </div>
      </div>

      {/* MOBILE: cinematic artist rail (replaces desktop mosaic on small screens) */}
      <div className="lg:hidden relative mt-2 mb-8" data-testid="hero-mobile-artist-rail">
        <div className="overflow-hidden">
          <div className="flex gap-4 animate-marquee-slow px-6 py-4">
            {[...MOSAIC, ...MOSAIC].map((tile, i) => (
              <div
                key={`rail-${tile.label}-${i}`}
                className={`relative w-36 h-48 shrink-0 rounded-2xl overflow-hidden border border-white/15 shadow-[0_20px_50px_-20px_rgba(0,0,0,0.9)] ${i % 2 ? 'rotate-2 translate-y-2' : '-rotate-2'}`}
              >
                <div className={`absolute -inset-0.5 bg-gradient-to-br ${tile.ring} opacity-50 blur-md -z-10`} />
                <img src={tile.src} alt={tile.label} className="w-full h-full object-cover" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent" />
                <div className="absolute top-2 right-2">
                  <span className="glass text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full text-amber-300 flex items-center gap-1">
                    <BadgeCheck className="h-2.5 w-2.5" /> {tile.badge}
                  </span>
                </div>
                <div className="absolute bottom-2 left-2 right-2">
                  <div className="text-white text-[11px] font-display font-semibold drop-shadow truncate">{tile.label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
        {/* edge fades */}
        <div className="pointer-events-none absolute inset-y-0 left-0 w-12 rail-fade-l" />
        <div className="pointer-events-none absolute inset-y-0 right-0 w-12 rail-fade-r" />
        {/* floating AI chip */}
        <div className="absolute -top-2 right-6 z-10 glass-strong rounded-full px-3 py-1.5 border border-cyan-400/30 shadow-xl flex items-center gap-1.5 text-[10px] animate-pulse-glow">
          <Sparkles className="h-3 w-3 text-cyan-300" />
          <span className="text-slate-200">AI matching</span>
          <span className="ai-text font-display font-bold text-xs">98%</span>
        </div>
      </div>

      {/* Stats strip */}
      <div className="relative max-w-7xl mx-auto px-6 sm:px-12 pb-12">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px rounded-3xl overflow-hidden border border-white/10 glass-strong">
          {[
            { value: '12,000+', label: 'Verified artists', icon: BadgeCheck, color: 'text-fuchsia-300' },
            { value: '180+', label: 'Cities worldwide', icon: Globe, color: 'text-cyan-300' },
            { value: '96%', label: 'Booking success', icon: TrendingUp, color: 'text-emerald-300' },
            { value: '24/7', label: 'AI assistant', icon: Sparkles, color: 'text-amber-300' },
          ].map((s) => (
            <div key={s.label} className="p-6 sm:p-8 bg-[#0B1020]/60 flex items-start gap-4">
              <s.icon className={`h-5 w-5 ${s.color} mt-1`} />
              <div>
                <div className="font-display text-2xl sm:text-3xl font-bold text-white tracking-tight">{s.value}</div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-slate-400 mt-1">{s.label}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Marquee — category rail */}
      <div className="relative border-y border-white/5 bg-[#0B1020]/60 py-5 overflow-hidden">
        <div className="flex gap-12 animate-marquee whitespace-nowrap">
          {[...Array(2)].map((_, dup) => (
            <div key={`rail-${dup}`} className="flex gap-12 items-center">
              {['Singers', 'Dancers', 'DJs', 'Actors', 'Models', 'Comedians', 'Photographers', 'Influencers', 'Musicians', 'Choreographers', 'Makeup Artists', 'Voice Artists'].map((cat) => (
                <span key={cat + dup} className="font-display text-2xl sm:text-3xl font-semibold text-slate-500 hover:text-white transition tracking-tight flex items-center gap-12">
                  {cat}
                  <span className="h-1.5 w-1.5 rounded-full bg-gradient-to-r from-fuchsia-500 to-cyan-500" />
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
